import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CalendarApi, CalendarOptions, EventInput } from '@fullcalendar/core/index.js';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';
import rrulePlugin from '@fullcalendar/rrule';
import timeGridPlugin from '@fullcalendar/timegrid';
import { differenceInMilliseconds, startOfToday } from 'date-fns';
import { filter, fromEvent, map, Subject, tap, throttleTime } from 'rxjs';
import { ClientEventResponseModel } from '../../../models/clients.model';
import { ceilNearest, nearestMinute } from '../../../utils/math';
import { CalendarViewType } from '../models/scheduler.model';
import {
  buildRRuleString,
  calculateDuration,
  formatLocalDateTime,
} from '../utils/rrule-utils';

interface BlockedRange {
  start: Date;
  end: Date;
  editorName: string;
  editorUserId: string;
}

@Injectable({
  providedIn: 'root',
})
export class CalendarManagerService {
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _calendarApi$ = new Subject<CalendarApi>();

  private _currentCalendarApi: CalendarApi | null = null;

  readonly calendarApi$ = this._calendarApi$.asObservable();

  /**
   * Set the calendar API instance
   */
  setCalendarApi(api: CalendarApi, emit = true): void {
    this._currentCalendarApi = api;

    if (emit) {
      this._calendarApi$.next(api);
    }
  }

  /**
   * Get the current calendar API instance
   */
  getCalendarApi(): CalendarApi | null {
    return this._currentCalendarApi;
  }

  /**
   * Navigate to the next period (day/week/month depending on view)
   */
  navigateNext(): void {
    if (!this._currentCalendarApi) {
      return;
    }

    this._currentCalendarApi.next();
  }

  /**
   * Navigate to the previous period (day/week/month depending on view)
   */
  navigatePrevious(): void {
    if (!this._currentCalendarApi) {
      return;
    }

    this._currentCalendarApi.prev();
  }

  /**
   * Navigate to today
   */
  navigateToToday(): void {
    if (!this._currentCalendarApi) {
      return;
    }

    this._currentCalendarApi.today();
  }

  /**
   * Navigate to a specific date
   */
  navigateToDate(date: Date): void {
    if (!this._currentCalendarApi) {
      return;
    }

    this._currentCalendarApi.gotoDate(date);
  }

  /**
   * Change the calendar view
   */
  changeView(view: CalendarViewType): void {
    if (!this._currentCalendarApi) {
      return;
    }

    this._currentCalendarApi.changeView(view);
  }

  /**
   * Render events in the calendar
   */
  renderEvents(events: ClientEventResponseModel[]): EventInput[] {
    return events.map((event) => this._makeCalendarEvent(event));
  }

  /**
   * Clear all events from the calendar (except background events)
   */
  clearEvents(): void {
    if (!this._currentCalendarApi) {
      return;
    }

    const events = this._currentCalendarApi.getEvents();
    events
      .filter((event) => event.display !== 'background')
      .forEach((event) => event.remove());
  }

  /**
   * Update the calendar event source
   */
  updateEventSource(events: EventInput[]): void {
    if (!this._currentCalendarApi) {
      return;
    }

    // Remove old event sources
    const sources = this._currentCalendarApi.getEventSources();
    sources.forEach((source) => source.remove());

    // Add new events
    this._currentCalendarApi.addEventSource(events);
  }

  /**
   * Remove all event sources efficiently (batch removal)
   */
  removeAllEventSources(): void {
    if (!this._currentCalendarApi) {
      return;
    }

    const sources = this._currentCalendarApi.getEventSources();
    sources.forEach((source) => source.remove());
  }

  /**
   * Render blocked ranges as background events
   */
  renderBlockedRanges(ranges: BlockedRange[]): void {
    if (!this._currentCalendarApi) {
      return;
    }

    // Remove existing blocked range events
    this.clearBlockedRanges();

    // Add new blocked ranges
    ranges.forEach((range, i) => {
      this._currentCalendarApi!.addEvent({
        id: `blocked-${i}`,
        start: range.start,
        end: range.end,
        color: 'red',
        backgroundColor: 'rgb(0 0 0 / .1)',
        display: 'background',
        editable: false,
        allDay: true,
      });
    });
  }

  /**
   * Clear all blocked range background events
   */
  clearBlockedRanges(): void {
    if (!this._currentCalendarApi) {
      return;
    }

    const existingBackgroundEvents = this._currentCalendarApi
      .getEvents()
      .filter(
        (event) => event.display === 'background' && event.id.startsWith('blocked-')
      );

    existingBackgroundEvents.forEach((event) => event.remove());
  }

  /**
   * Scroll to a specific time (in milliseconds since start of day)
   */
  scrollToTime(milliseconds?: number): void {
    if (!this._currentCalendarApi) {
      return;
    }

    const timeToScroll = milliseconds ?? this.getTimeElapsedSinceToday();
    this._currentCalendarApi.scrollToTime(nearestMinute(timeToScroll, 30));
  }

  /**
   * Scroll to the current time
   */
  scrollToCurrentTime(): void {
    this.scrollToTime();
  }

  /**
   * Get the active date range of the current calendar view
   */
  getActiveRange(): { start: Date; end: Date } {
    if (!this._currentCalendarApi) {
      return { start: new Date(), end: new Date() };
    }

    return {
      start: this._currentCalendarApi.view.activeStart,
      end: this._currentCalendarApi.view.activeEnd,
    };
  }

  /**
   * Get the time elapsed since today (start of day) in milliseconds
   */
  getTimeElapsedSinceToday(): number {
    const now = new Date();
    const startOfDay = startOfToday();

    return differenceInMilliseconds(now, startOfDay);
  }

  /**
   * Setup wheel event handling for month navigation
   */
  setupWheelEventHandling(
    calendarElement: HTMLElement,
    currentView: () => CalendarViewType
  ): void {
    fromEvent<WheelEvent>(calendarElement, 'wheel')
      .pipe(
        throttleTime(300),
        filter(() => currentView() === 'dayGridMonth'),
        filter((event) => this._shouldAllowMonthNavigation(event)),
        map((event) => event.deltaY),
        tap((deltaY) => {
          if (deltaY < 0) {
            this.navigatePrevious();
            return;
          }

          this.navigateNext();
        }),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe();
  }

  /**
   * Remove an event by ID
   */
  removeEvent(eventId: string): void {
    if (!this._currentCalendarApi) {
      return;
    }

    const event = this._currentCalendarApi.getEventById(eventId);
    event?.remove();
  }

  /**
   * Update calendar size
   */
  updateSize(): void {
    if (!this._currentCalendarApi) {
      return;
    }

    this._currentCalendarApi.updateSize();
  }

  /**
   * Unselect the current date selection
   */
  unselect(): void {
    if (!this._currentCalendarApi) {
      return;
    }

    this._currentCalendarApi.unselect();
  }

  /**
   * Select a date range in the calendar
   */
  selectDateRange(start: Date, end: Date): void {
    if (!this._currentCalendarApi) {
      return;
    }

    this._currentCalendarApi.select(start, end);
  }

  /**
   * Get all calendar events
   */
  getEvents() {
    if (!this._currentCalendarApi) {
      return [];
    }

    return this._currentCalendarApi.getEvents();
  }

  /**
   * Build calendar options with event handlers
   */
  buildCalendarOptions(handlers: Partial<CalendarOptions>): CalendarOptions {
    return {
      allDaySlot: false,
      editable: true,
      eventTimeFormat: {
        hour: 'numeric',
        minute: '2-digit',
      },
      firstDay: 1,
      headerToolbar: false,
      initialView: 'dayGridMonth',
      locale: 'nl',
      nowIndicator: true,
      plugins: [
        dayGridPlugin,
        interactionPlugin,
        listPlugin,
        rrulePlugin,
        timeGridPlugin,
      ],
      timeZone: 'local',
      scrollTimeReset: false,
      selectable: true,
      selectMirror: true,
      selectConstraint: {
        startTime: '00:00',
        endTime: '24:00',
      },
      slotDuration: '00:05:00',
      slotLabelInterval: '00:30:00',
      slotLabelFormat: {
        hour: 'numeric',
        minute: '2-digit',
      },
      unselectCancel: '.cdk-overlay-container',
      weekNumbers: true,
      ...handlers,
    };
  }

  /**
   * Round duration to nearest 5 minutes (minimum 5 minutes)
   */
  roundToNearest5Minutes(durationInMs: number): number {
    const MIN_EVENT_DURATION = 3e5; // 5 minutes in ms
    return Math.max(ceilNearest(durationInMs, MIN_EVENT_DURATION), MIN_EVENT_DURATION);
  }

  private _makeCalendarEvent(value: ClientEventResponseModel): EventInput {
    if (value.recurrence == null) {
      return this._makeSingleCalendarEvent(value);
    }

    return this._makeRecurrentCalendarEvent(value);
  }

  private _makeRecurrentCalendarEvent(value: ClientEventResponseModel): EventInput {
    const recurrence = value.recurrence!;

    const startDate = new Date(value.startDate);
    const endDate = new Date(value.endDate);
    const untilDate = recurrence.until ? new Date(recurrence.until) : null;

    const typeOfEvent = this._getEventType(value);

    const rruleString = buildRRuleString({
      dtstart: formatLocalDateTime(startDate),
      freq: recurrence.freq,
      interval: recurrence.interval ?? 1,
      byweekday: recurrence.byweekday,
      bysetpos: recurrence.bysetpos ?? null,
      bymonthday: recurrence.bymonthday ?? null,
      until: untilDate ? formatLocalDateTime(untilDate) : null,
    });

    const duration = calculateDuration(startDate, endDate);

    return {
      id: value._id,
      title: value.interaction.label,
      rrule: rruleString,
      exdate: value.exdates.map((date) => formatLocalDateTime(new Date(date))),
      duration,
      classNames: [typeOfEvent, 'recurring'],
    };
  }

  private _makeSingleCalendarEvent(value: ClientEventResponseModel): EventInput {
    const typeOfEvent = this._getEventType(value);

    return {
      id: value._id,
      title: value.interaction.label,
      start: value.startDate,
      end: value.endDate,
      classNames: [typeOfEvent],
    };
  }

  private _getEventType(event: ClientEventResponseModel): string {
    const { interaction } = event;

    switch (interaction.type) {
      case 'media-image':
        return 'event-media-image';
      case 'media-audio':
        return 'event-media-audio';
      case 'with-images':
        return 'event-question-and-answers--with-images';
      case 'with-answers':
        return 'event-question-and-answers';
      case 'questions-only':
      default:
        return 'event-announcement';
    }
  }

  private _shouldAllowMonthNavigation(event: WheelEvent): boolean {
    const rootElement = event.currentTarget as HTMLElement;
    const element = rootElement.querySelector(
      '.fc-scrollgrid-section-body .fc-scroller'
    );

    if (!element) {
      return false;
    }

    const { scrollTop, scrollHeight, clientHeight } = element;
    const isScrollable = scrollHeight > clientHeight;

    if (!isScrollable) {
      return true;
    }

    const isAtTop = scrollTop === 0;
    const isAtBottom = scrollTop + clientHeight >= scrollHeight - 1; // -1 for rounding tolerance
    const scrollingUp = event.deltaY < 0;
    const scrollingDown = event.deltaY > 0;

    return (scrollingUp && isAtTop) || (scrollingDown && isAtBottom);
  }
}
